import Sanitize from './vendor/sanitize';
import showdown from './vendor/showdown';
import { $, createElement, fireEvent, on, toggleElement } from './util';

function isBlank (str) { return str.trim() === ''; }

if (!Function.prototype.debounce) {
  Function.prototype.debounce = function (interval) {
    this._timeout = null;
    let that = this;
    return function () {
      let args = arguments;
      if (that._timeout) { clearTimeout(that._timeout); }
      that._timeout = setTimeout(function () {
        that.apply(null, args);
        that._timeout = null;
      }, interval);
    };
  };
}

if (!Sanitize.Config) {
  Sanitize.Config = {};
}

Sanitize.Config.CUSTOM = {
  elements: [
    'a', 'abbr', 'acronym', 'b', 'blockquote', 'cite', 'code', 'del', 'em', 'i',
    'li', 'ol','p', 'pre', 'q', 's', 'strike', 'strong', 'ul'],

  attributes: {
    'a'         : ['href', 'title'],
    'abbr'      : ['title'],
    'acronym'   : ['title'],
    'blockquote': ['cite'],
    'code'      : ['class'],
    'del'       : ['datetime'],
    'q'         : ['cite']
  },

  add_attributes: {
    'a': {'rel': 'nofollow'}
  },

  protocols: {
    'a' : {'href': ['ftp', 'http', 'https', 'mailto', Sanitize.RELATIVE]},
    'q':  {'cite': ['http', 'https', Sanitize.RELATIVE]},
    'blockquote': {'cite': ['http', 'https', Sanitize.RELATIVE]}
  }
};

// A replacement for Prototype's Form.Observer.
class FormObserver {
  constructor (form, frequencyInSeconds, callback) {
    this.fields = [];
    for (let field of [...form.elements]) {
      if (field.matches('input[type="hidden"], input[type="submit"], button')) { continue; }
      this.fields.push(field);
    }
    this.frequency = frequencyInSeconds;
    this.callback = callback;
    this._last = this._serialize();
    this.observe();
  }

  observe () {
    this._interval = setInterval(this._check.bind(this), this.frequency * 1000);
  }

  stopObserving () {
    clearInterval(this._interval);
  }

  _check () {
    let current = this._serialize();
    if (current !== this._last) {
      this.callback();
      this._last = current;
    }
  }

  _serialize () {
    return this.fields.map(f => f.value).join(',');
  }


}

class Previewer {
  constructor (element, form) {
    this.element = $(element);
    if (!this.element) return;
    this.form = $(form);

    this.dummy = createElement('div', {
      'data-preview-wrapper': 'true'
    });

    this.findFields();
    this.findSlots();

    this.addObservers();

    this.converter = new showdown.Converter(Previewer.SHOWDOWN_OPTIONS);
    this.sanitizer = new Sanitize(Sanitize.Config.CUSTOM);

    this.debouncedUpdatePreview = this.updatePreview.bind(this).debounce(200);
    this.update();
  }

  addObservers () {
    this.observers = {
      form: new FormObserver(this.form, 0.5, this.update.bind(this))
    };
  }

  update () {
    let { author, comment } = this.getValues();
    this.findSlots();
    let s = this.slots;

    let hasAuthorField = s.author;
    let hasCommentField = s.comment;

    if (hasAuthorField) {
      s.author.classList.toggle('js-blank', isBlank(author));
    }

    if (hasCommentField) {
      s.comment.classList.toggle('js-blank', isBlank(comment));
    }

    toggleElement(this.element, !isBlank(comment || ''));

    if (hasAuthorField) {
      s.author.innerText = author;
    }

    if ( comment && isBlank(comment)) {
      // We don't need to debounce. All we're doing is blanking out the field.
      this.updatePreview(comment);
    } else {
      this.debouncedUpdatePreview(comment);
    }
  }

  updatePreview (markdown) {
    if (!this.slots.comment) { return; }
    // Turn markdown into HTML.
    let html;
    if ( isBlank(markdown) ) {
      html = markdown;
    } else {
      html = this.converter.makeHtml(markdown);
    }

    if ( !html || isBlank(html) ) {
      let hadHTML = !isBlank(this.slots.comment.innerHTML);
      this.slots.comment.innerHTML = '';
      if (hadHTML) {
        fireEvent(this.element, 'preview:updated');
      }
      return;
    }
    // Put the HTML into a dummy element that isn't attached to the page.
    // This is necessary because the sanitizer works with a DOM structure,
    // not an HTML string.
    this.dummy.innerHTML = html;

    // The sanitizer returns a document fragment ready for insertion.
    let fragment = this.sanitizer.clean_node(this.dummy);

    this.slots.comment.innerHTML = '';
    this.slots.comment.appendChild(fragment);

    fireEvent(this.element, 'preview:updated');
  }

  findFields () {
    this.fields = {
      url: $('url'),
      author: $('author') || this.form.querySelector('[data-logged-in-as]'),
      comment: this.form.querySelector('textarea')
    };
  }

  findSlots () {
    this.slots = {
      author:  this.element.querySelector('[data-preview-author]'),
      comment: this.element.querySelector('[data-preview-body]')
    };
  }

  getValues () {
    if (!this.fields) { throw new Error('Fields not initialized!'); }
    let f = this.fields;
    let values = {};

    if (f.author) {
      values.author = ('value' in f.author) ?
        f.author.value : f.author.innerHTML;
      if (!values.author) {
        values.author = $('openid_identifier').value;
      }
    }

    if (f.url)     { values.url     = f.url.value;     }
    if (f.comment) { values.comment = f.comment.value; }
    return values;
  }
}

Previewer.SHOWDOWN_OPTIONS = {
  noHeaderId: true,
  ghCodeBlocks: true,
  strikethrough: true,
  headerLevelStart: 3,
  smoothLivePreview: true,
  omitExtraWLInCodeBlocks: true,
  literalMidWordUnderscores: true
};

const Credentials = {
  setup: function () {
    this.parent = document.querySelector('[data-login-parent]');
    if (!this.parent) { return; }
    this.panels = {};

    this.fields = {
      author:  $('author'),
      email:   $('email'),
      openid:  $('openid_identifier'),
      website: $('url')
    };

    let panels = document.querySelectorAll('[data-login]');

    [...panels].forEach((p) => {
      let name = p.getAttribute('data-login');
      this.panels[name] = p;
    });

    $('openid_checkbox').addEventListener('change', (e) => {
      setTimeout(this.onToggle.bind(this), 0);
      let form = e.target.closest('form');
      fireEvent(form, 'change:login');
    });

    this.onToggle();
  },

  getActive: function () {
    return $('openid_checkbox').checked ? 'openid' : 'website';
  },

  onToggle: function () {
    let active = this.getActive();
    let { parent, fields: f } = this;
    parent.classList.toggle('js-active-website', active === 'website');
    parent.classList.toggle('js-active-openid', active === 'openid');

    f.author.required = (active === 'website');
    f.email.required  = (active === 'website');

    f.openid.disabled  = (active === 'website');
    f.website.disabled = (active === 'openid');
  }
};

const Highlighter = {
  setup: function (element) {
    this.element = $(element);
    if (!this.element) { return; }

    this._debouncedHighlight = this.highlight.bind(this).debounce(500);

    this.element.addEventListener('preview:updated', this._debouncedHighlight);
  },

  highlight: function () {
    fireEvent(this.element, 'highlighter:rescan');
  }
};

const Quoter = {
  // For when the quoted representation of an element should be different
  // from its outerHTML.
  // Each transformation receives a node and returns text.
  transformations: {
    // Convert code blocks back into the GitHub-Flavored Markdown used to
    // produce them. Otherwise we try to quote the HTML and everything goes
    // sideways.
    'pre': function (pre) {
      let language = pre.getAttribute('data-language') || '';
      let text = `\`\`\`${language}\n${pre.innerText}\n\`\`\``;
      return text;
    }
  },

  setup: function () {
    this.list = document.querySelector('[data-comments-list]');
    if (!this.list) { return; }

    this.comment = $('comment');
    if (!this.comment) { return; }

    this.sanitizer = new Sanitize(Sanitize.Config.CUSTOM);
    this.dummy = createElement('div');

    on(this.list, 'click', '[data-quote-link]', (e) => {
      let comment = e.target.closest('[data-comment]');
      let body = comment.querySelector('[data-comment-body]');
      let url = comment.querySelector('[data-permalink]').getAttribute('href');

      let text = this.sanitize(body);
      text = this.format(text, url);

      this.comment.value += text;
      this.comment.focus();
    });
  },

  sanitize: function (node) {
    this.dummy.innerHTML = '';
    this.dummy.appendChild( node.cloneNode(true) );

    for (let selector in this.transformations) {
      let handler = this.transformations[selector];
      let matches = this.dummy.querySelectorAll(selector);
      [...matches].forEach((el) => {
        let replacement = handler(el) || '';
        el.parentNode.replaceChild(document.createTextNode(replacement), el);
      });
    }

    // The sanitizer returns a document fragment ready for insertion.
    let fragment = this.sanitizer.clean_node(this.dummy);

    this.dummy.innerHTML = '';
    this.dummy.appendChild(fragment);

    return this.dummy.innerHTML;
  },

  format: function (text, url) {
    text = text.trim();
    return `<blockquote cite="${url}">${text}</blockquote>\n\n`;
  }
};

const Validator = {
  setup: function () {
    this.form = document.querySelector('[data-comment-form]');
    if (!this.form) return;

    if (typeof this.form.checkValidity !== 'function') { return; }

    on(this.form, 'submit', this.checkValidity.bind(this));
    this.fields = [...this.form.querySelectorAll('input, textarea')];

    for (let field of this.fields) {
      on(field, 'invalid', this.onInvalidField.bind(this));
      on(field, 'blur', this.checkFieldValidity.bind(this));
    }

    on(this.form, 'change:login', () => {
      setTimeout(() => {
        this.fields.forEach(this.checkFieldValidity, this);
      }, 0);
    });
  },

  checkFieldValidity: function (e) {
    let el = e.nodeType ? e : e.target;
    let isValid = el.checkValidity();
    el.classList.toggle('u-invalid', !isValid);
  },

  onInvalidField: function (e) {
    e.target.classList.add('u-invalid');
  },

  // Check form validity.
  checkValidity: function (e) {
    if ( !this.form.checkValidity() ) {
      e.preventDefault();
      setTimeout(() => {
        this.form.querySelector('.u-invalid').focus();
      }, 0);
    }
  }
};

export {
  Credentials,
  Highlighter,
  Previewer,
  Quoter,
  Validator
};
